import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useEmbedContext } from '@/hooks/useEmbedContext';
import { useQuestion } from '@/hooks/useQuestions';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/StatusBadge';
import { formatDateTime, getSourceLabel } from '@/lib/utils';

export function QuestionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { bridge } = useEmbedContext();
  const { data: question, isLoading, isError, error } = useQuestion(id);

  // Set page title via bridge
  useEffect(() => {
    if (question) {
      bridge?.setTitle(question.title || 'Question Details');
    }
  }, [bridge, question]);

  if (isLoading) {
    return (
      <div className="space-y-4 p-3 sm:space-y-6 sm:p-6">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !question) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={() => navigate('/questions')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Questions
        </Button>
        <div className="mt-8 flex h-64 items-center justify-center text-destructive">
          {error?.message || 'Question not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-3 sm:space-y-6 sm:p-6">
      <Button variant="ghost" onClick={() => navigate('/questions')}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Questions
      </Button>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <CardTitle className="text-xl">
                {question.title || 'Question'}
              </CardTitle>
              <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                <StatusBadge status={question.status} />
                {question.source && (
                  <span>via {getSourceLabel(question.source)}</span>
                )}
                <span>{formatDateTime(question.timestamp)}</span>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Question */}
          <div>
            <h3 className="mb-2 font-semibold text-muted-foreground">
              Question
            </h3>
            <p className="whitespace-pre-wrap">{question.question_text}</p>
          </div>

          {/* Answer */}
          {question.answer_text && (
            <div>
              <h3 className="mb-2 font-semibold text-muted-foreground">
                Answer
              </h3>
              <div className="rounded-lg bg-muted/50 p-4">
                <p className="whitespace-pre-wrap">{question.answer_text}</p>
              </div>
              {question.answered_by && (
                <p className="mt-2 text-sm text-muted-foreground">
                  Answered by {question.answered_by.doctor_name}
                  {question.answered_by.answered_at &&
                    ` on ${formatDateTime(question.answered_by.answered_at)}`}
                </p>
              )}
            </div>
          )}

          {/* Metadata */}
          <div className="grid gap-4 border-t pt-4 sm:grid-cols-2 lg:grid-cols-3">
            {question.asker_name && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">
                  Asker
                </h4>
                <p>{question.asker_name}</p>
              </div>
            )}
            {question.category && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">
                  Category
                </h4>
                <p>{question.category}</p>
              </div>
            )}
            {question.group_name && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">
                  Group
                </h4>
                <p>{question.group_name}</p>
              </div>
            )}
            {question.assigned_doctor && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">
                  Assigned To
                </h4>
                <p>{question.assigned_doctor.doctor_name}</p>
              </div>
            )}
            {question.tags && question.tags.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">
                  Tags
                </h4>
                <p>{question.tags.join(', ')}</p>
              </div>
            )}
            {question.priority && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">
                  Priority
                </h4>
                <p className="capitalize">{question.priority}</p>
              </div>
            )}
          </div>

          {/* Rejection Note */}
          {question.rejection_note && (
            <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4">
              <h4 className="mb-1 font-medium text-destructive">
                Rejection Note
              </h4>
              <p>{question.rejection_note}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
